import type { MapData, MapDataLocationFeature } from '@repo/types/maps/map-types';
import { getPinImageUrl } from '@repo/utility/image-urls';

export type CompletionFilter = 'all' | 'completed' | 'uncompleted';

export function buildCategoryMap(mapData: MapData) {
    return new Map(mapData.metadata.categories.map(cat => [cat.externalId, cat]));
}

export function buildSubcategoryMap(mapData: MapData) {
    return new Map(mapData.metadata.subcategories.map(sub => [sub.externalId, sub]));
}

export function isIncludedInChecklist(
    subcategory: { excludeFromChecklists?: boolean },
    category: { excludeFromChecklists?: boolean },
): boolean {
    return !subcategory.excludeFromChecklists && !category.excludeFromChecklists;
}

export interface SubcategoryStats {
    subcategoryId: string;
    title: string;
    description?: string;
    categoryTitle: string;
    categoryId: string;
    icon: string;
    totalItems: number;
    foundItems: number;
}

export interface CategoryGroup {
    categoryId: string;
    title: string;
    subcategories: SubcategoryStats[];
}

export interface ChecklistItem {
    id: string;
    title: string;
    description: string;
    categoryTitle: string;
    subcategoryTitle: string;
    subcategoryExternalId: string;
    categoryColor: string;
    imagePath: string | null;
    imageId: number | null;
    imageUrl: string | null;
    coordinates: [number, number];
}

export function buildCategoryGroups(mapData: MapData, checkedItems: Set<string>): CategoryGroup[] {
    const categoryMap = buildCategoryMap(mapData);
    const subcategoryMap = buildSubcategoryMap(mapData);
    const subcategoryStatsMap = new Map<string, SubcategoryStats>();
    const categoryGroupsMap = new Map<string, CategoryGroup>();

    mapData.geojson.features.forEach((feature: MapDataLocationFeature) => {
        const subcategory = subcategoryMap.get(feature.properties.subcategoryExternalId);
        const category = categoryMap.get(subcategory?.categoryExternalId ?? '');

        if (!category || !subcategory || !isIncludedInChecklist(subcategory, category)) {
            return;
        }

        let stats = subcategoryStatsMap.get(subcategory.externalId);
        if (!stats) {
            stats = {
                subcategoryId: subcategory.externalId,
                title: subcategory.title,
                description: subcategory.description || '',
                categoryTitle: category.title,
                categoryId: category.externalId,
                icon: subcategory.icon || 'category',
                totalItems: 0,
                foundItems: 0,
            };
            subcategoryStatsMap.set(subcategory.externalId, stats);

            let categoryGroup = categoryGroupsMap.get(category.externalId);
            if (!categoryGroup) {
                categoryGroup = {
                    categoryId: category.externalId,
                    title: category.title,
                    subcategories: [],
                };
                categoryGroupsMap.set(category.externalId, categoryGroup);
            }
            categoryGroup.subcategories.push(stats);
        }

        stats.totalItems++;
        if (checkedItems.has(feature.properties.externalId)) {
            stats.foundItems++;
        }
    });

    const groups = Array.from(categoryGroupsMap.values());
    groups.forEach(group => {
        group.subcategories.sort((a, b) => a.title.localeCompare(b.title));
    });
    groups.sort((a, b) => a.title.localeCompare(b.title));

    return groups;
}

export function filterCategoryGroups(
    categoryGroups: CategoryGroup[],
    searchQuery: string,
    completionFilter: CompletionFilter = 'all',
): CategoryGroup[] {
    const query = searchQuery.trim().toLowerCase();
    if (!query && completionFilter === 'all') {
        return categoryGroups;
    }

    return categoryGroups
        .map(group => {
            const matchesCategory = !!query && group.title.toLowerCase().includes(query);
            const filteredSubcategories = group.subcategories.filter(subcategory => {
                const matchesQuery =
                    !query ||
                    matchesCategory ||
                    subcategory.title.toLowerCase().includes(query) ||
                    subcategory.description?.toLowerCase().includes(query);
                if (!matchesQuery) return false;
                if (completionFilter === 'completed') {
                    return subcategory.totalItems > 0 && subcategory.foundItems === subcategory.totalItems;
                }
                if (completionFilter === 'uncompleted') {
                    return subcategory.foundItems < subcategory.totalItems;
                }
                return true;
            });

            if (filteredSubcategories.length === 0) {
                return null;
            }

            return {
                ...group,
                subcategories: filteredSubcategories,
            };
        })
        .filter((group): group is CategoryGroup => group !== null);
}

export function buildSubcategoryImageMap(mapData: MapData, size = 250): Map<string, string | null> {
    const imageMap = new Map<string, string | null>();

    mapData.geojson.features.forEach((feature: MapDataLocationFeature) => {
        const subcategoryId = feature.properties.subcategoryExternalId;
        if (!imageMap.has(subcategoryId)) {
            const imageUrl = getPinImageUrl(feature.properties.imageId, feature.properties.imagePath, size);
            if (imageUrl) {
                imageMap.set(subcategoryId, imageUrl);
            }
        }
    });

    return imageMap;
}

export function computeChecklistTotals(categoryGroups: CategoryGroup[]): {
    totalChecklistItems: number;
    foundChecklistItems: number;
} {
    let totalChecklistItems = 0;
    let foundChecklistItems = 0;

    for (const group of categoryGroups) {
        for (const subcat of group.subcategories) {
            totalChecklistItems += subcat.totalItems;
            foundChecklistItems += subcat.foundItems;
        }
    }

    return { totalChecklistItems, foundChecklistItems };
}

export function buildCheckedItems(
    isLoggedIn: boolean,
    foundPinsData: Array<{ found: boolean; pinnableId: string }> | undefined,
): Set<string> {
    if (!isLoggedIn || !foundPinsData) return new Set<string>();
    return new Set(foundPinsData.filter(pin => pin.found).map(pin => pin.pinnableId));
}

export function buildChecklistItems(
    mapData: MapData,
    options?: { subcategoryId?: string; imageSize?: number },
): ChecklistItem[] {
    const { subcategoryId, imageSize } = options ?? {};

    if (!mapData?.geojson?.features || !Array.isArray(mapData.geojson.features)) {
        return [];
    }

    const subcategoryMap = buildSubcategoryMap(mapData);
    const categoryMap = buildCategoryMap(mapData);
    const items: ChecklistItem[] = [];

    mapData.geojson.features.forEach((feature: MapDataLocationFeature) => {
        if (!feature?.properties?.externalId || !feature?.geometry?.coordinates) return;
        if (subcategoryId && feature.properties.subcategoryExternalId !== subcategoryId) return;

        const subcategory = subcategoryMap.get(feature.properties.subcategoryExternalId);
        const category = subcategory ? categoryMap.get(subcategory.categoryExternalId) : null;

        if (subcategory && category && isIncludedInChecklist(subcategory, category)) {
            items.push({
                id: feature.properties.externalId,
                title: feature.properties.title || subcategory.title,
                description: feature.properties.description || '',
                categoryTitle: category.title,
                subcategoryTitle: subcategory.title,
                subcategoryExternalId: subcategory.externalId,
                categoryColor: category.color,
                imagePath: feature.properties.imagePath,
                imageId: feature.properties.imageId,
                imageUrl: getPinImageUrl(feature.properties.imageId, feature.properties.imagePath, imageSize),
                coordinates: feature.geometry.coordinates,
            });
        }
    });

    return items.sort((a, b) => {
        if (a.categoryTitle !== b.categoryTitle) {
            return a.categoryTitle.localeCompare(b.categoryTitle);
        }

        if (a.subcategoryTitle !== b.subcategoryTitle) {
            return a.subcategoryTitle.localeCompare(b.subcategoryTitle);
        }

        return a.title.localeCompare(b.title);
    });
}

export function filterChecklistItems(
    items: ChecklistItem[],
    searchQuery: string,
    completionFilter: CompletionFilter,
    checkedItems: Set<string>,
): ChecklistItem[] {
    let filtered = items;

    if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        filtered = filtered.filter(
            item =>
                item.title.toLowerCase().includes(query) ||
                item.description.toLowerCase().includes(query) ||
                item.categoryTitle.toLowerCase().includes(query) ||
                item.subcategoryTitle.toLowerCase().includes(query),
        );
    }

    if (completionFilter === 'completed') {
        filtered = filtered.filter(item => checkedItems.has(item.id));
    } else if (completionFilter === 'uncompleted') {
        filtered = filtered.filter(item => !checkedItems.has(item.id));
    }

    return filtered;
}

export function groupItemsByCategory(items: ChecklistItem[]): Record<string, ChecklistItem[]> {
    const groups: Record<string, ChecklistItem[]> = {};

    items.forEach(item => {
        if (!groups[item.categoryTitle]) {
            groups[item.categoryTitle] = [];
        }
        groups[item.categoryTitle]?.push(item);
    });

    return groups;
}
